import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStudy } from '../context/StudyContext';
import { apiService } from '../services/api';

const StudyPack = () => {
  const navigate = useNavigate();
  const { getTopicStats, isLoading } = useStudy();
  
  const [progressData, setProgressData] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [topics, setTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);

  useEffect(() => {
    fetchTopicsFromAPI();
    fetchProgressData();
  }, []);

  const fetchTopicsFromAPI = async () => {
    setLoadingTopics(true);
    try {
      const topicsData = await apiService.getTopics();
      console.log('Topics fetched from API:', topicsData);
      
      if (topicsData && topicsData.length > 0) {
        setTopics(topicsData);
      } else {
        console.log('No topics data received from API');
        setTopics([]);
      }
    } catch (error) {
      console.error('Error fetching topics from API:', error);
      setTopics([]);
    } finally {
      setLoadingTopics(false);
    }
  };

  const fetchProgressData = async () => {
    setLoadingProgress(true);
    try {
      const data = await apiService.getProgress('all');
      console.log('Progress data for StudyPack:', data);
      setProgressData(data);
    } catch (error) {
      console.error('Error fetching progress data:', error);
      setProgressData({
        answered: 0,
        mastery_percentage: 0,
        mastery_breakdown: {
          almost_there: 0,
          needs_work: 0
        },
        completion_percentage: 0
      });
    } finally {
      setLoadingProgress(false);
    }
  };

  const topicStats = getTopicStats ? getTopicStats() : {};

  const totalQuestions = topics.reduce((sum, topic) => {
    try {
      return sum + (topic?.questions || 0);
    } catch (error) {
      console.error('Error calculating total questions:', error);
      return sum;
    }
  }, 0);

  const totalAnswered = progressData?.answered || 0;
  
  const masteredPercentage = progressData?.mastery_percentage || 0;
  const almostTherePercentage = totalAnswered > 0 ? Math.round(((progressData?.mastery_breakdown?.almost_there || 0) / totalAnswered) * 100) : 0;
  const needsWorkPercentage = totalAnswered > 0 ? Math.round(((progressData?.mastery_breakdown?.needs_work || 0) / totalAnswered) * 100) : 0;

  const handleTopicClick = (topicId) => {
    if (!topicId) return;
    navigate('/custom-study', { state: { selectedTopics: [topicId] } });
  };

  const getCompletionPercentage = (topic) => {
    if (!topic || !topic.questions) return 0;
    
    try {
      const stats = topicStats[topic.id] || { answered: 0 };
      const totalQuestions = topic.questions || 0;
      const answeredQuestions = stats.answered || 0;
      
      if (totalQuestions === 0) return 0;
      return Math.round((answeredQuestions / totalQuestions) * 100);
    } catch (error) {
      console.error('Error calculating completion percentage:', error);
      return 0;
    }
  };

  if (isLoading || loadingProgress || loadingTopics) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>
          <div style={styles.spinner}></div>
          <div style={styles.loadingText}>Loading study materials...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />
      <link href="https://cdn.materialdesignicons.com/7.4.47/css/materialdesignicons.min.css" rel="stylesheet" />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div style={styles.titleSection}>
            <h1 style={styles.title}>Study-Pack</h1>
            <p style={styles.subtitle}>Premium materials to help you learn and prepare for your exam</p>
          </div>
          
          <div style={styles.statsContainer}>
            <div style={styles.statItem}>
              <i className="mdi mdi-crown" style={styles.masteredIcon}></i>
              <div style={styles.statValue}>{masteredPercentage}%</div>
              <div style={styles.statLabel}>Mastered</div>
            </div>
            
            <div style={styles.statSpacer}></div>
            
            <div style={styles.statItem}>
              <i className="mdi mdi-progress-clock" style={styles.almostThereIcon}></i>
              <div style={styles.statValue}>{almostTherePercentage}%</div>
              <div style={styles.statLabel}>Almost There</div>
            </div>
            
            <div style={styles.statSpacer}></div>
            
            <div style={styles.statItem}>
              <i className="mdi mdi-alert-circle" style={styles.needsWorkIcon}></i>
              <div style={styles.statValue}>{needsWorkPercentage}%</div>
              <div style={styles.statLabel}>Needs Work</div>
            </div>
          </div>
        </div>
      </div>

      <div style={styles.divider}></div>

      <div style={styles.section}>
        <div style={styles.sectionHeader}>
          <div style={styles.sectionTitle}>
            {topics.length} {topics.length === 1 ? 'topic' : 'topics'} • {totalQuestions} items
          </div>
        </div>

        <div style={styles.topicList}>
          {topics.map((topic, index) => {
            if (!topic || !topic.id) {
              return (
                <div key={`invalid-${index}`} style={{display: 'none'}} />
              );
            }
            
            const completionPercentage = getCompletionPercentage(topic);
            
            return (
              <div 
                key={topic.id} 
                style={styles.topicCard}
                onClick={() => handleTopicClick(topic.id)}
                className="topic-card"
              >
                <div style={styles.progressCircle}>
                  <svg width="44" height="44" viewBox="0 0 44 44">
                    <circle cx="22" cy="22" r="18" fill="none" stroke="#e0e0e0" strokeWidth="4"/>
                    <circle 
                      cx="22" 
                      cy="22" 
                      r="18" 
                      fill="none" 
                      stroke={completionPercentage >= 80 ? '#28a745' : completionPercentage >= 60 ? '#ffc107' : '#dc3545'} 
                      strokeWidth="4"
                      strokeDasharray={`${(completionPercentage * 113) / 100} 113`}
                      transform="rotate(-90 22 22)"
                    />
                  </svg>
                  <div style={styles.progressText}>{completionPercentage}%</div>
                </div>
                <div style={styles.topicContent}>
                  <div style={styles.topicName}>{topic.name || 'Unnamed Topic'}</div>
                  <div style={styles.topicStats}>{(topic.questions || 0)} questions</div>
                </div>
                <div style={styles.arrowIcon}>
                  <i className="mdi mdi-chevron-right"></i>
                </div>
              </div>
            );
          })}

          {topics.length === 0 && (
            <div style={styles.emptyState}>
              <i className="mdi mdi-book-open-variant" style={styles.emptyIcon}></i>
              <div style={styles.emptyText}>No topics available</div>
              <button 
                style={styles.retryButton}
                onClick={fetchTopicsFromAPI}
              >
                Retry Loading Topics
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`
        .topic-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
          border-color: #2c5aa0;
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};
const styles = {
  container: {
    backgroundColor: '#f8f9fa',
    minHeight: '100vh',
    padding: '20px',
    fontFamily: '"Open Sans", sans-serif',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '60vh',
    fontSize: '16px',
    color: '#666',
    gap: '20px'
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #2c5aa0',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    fontSize: '18px',
    color: '#2c5aa0',
    fontWeight: '500',
    fontFamily: '"Open Sans", sans-serif',
  },
  header: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    padding: '30px',
    marginBottom: '25px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0'
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  titleSection: {
    flex: 1
  },
  title: {
    fontSize: '32px',
    fontWeight: '600',
    margin: '0 0 8px 0',
    color: '#2c5aa0',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0px',
    lineHeight: '1.2'
  },
  subtitle: {
    fontSize: '16px',
    margin: '0',
    color: '#666666',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.25px',
    lineHeight: '1.5'
  },
  statsContainer: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '400px',
    flexShrink: 0,
    gap: '0'
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexGrow: 1,
    minWidth: '80px'
  },
  statSpacer: {
    width: '15px',
    height: '1px'
  },
  masteredIcon: {
    fontSize: '28px',
    color: '#28a745',
    marginBottom: '8px'
  },
  almostThereIcon: {
    fontSize: '28px',
    color: '#ffc107',
    marginBottom: '8px'
  },
  needsWorkIcon: {
    fontSize: '28px',
    color: '#dc3545',
    marginBottom: '8px'
  },
  statValue: {
    fontSize: '18px',
    color: '#333333',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.25px',
    lineHeight: '20px',
    textAlign: 'center',
    fontWeight: '600',
    marginBottom: '4px'
  },
  statLabel: {
    fontSize: '12px',
    color: '#666666',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.5px',
    fontWeight: '500',
    lineHeight: '16px',
    textAlign: 'center'
  },
  divider: {
    height: '1px',
    backgroundColor: '#e0e0e0',
    margin: '20px 0',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto'
  },
  section: {
    maxWidth: '1200px',
    margin: '0 auto 30px auto'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
    padding: '20px 0'
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#2c5aa0',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.1px',
    lineHeight: '20px'
  },
  topicList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  topicCard: {
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    border: '1px solid #e0e0e0',
    position: 'relative'
  },
  progressCircle: {
    position: 'relative',
    width: '44px',
    height: '44px',
    flexShrink: 0
  },
  progressText: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: '10px',
    fontWeight: '600',
    color: '#333333',
    fontFamily: '"Open Sans", sans-serif'
  },
  topicContent: {
    flex: 1
  },
  topicName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333333',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0px',
    lineHeight: '20px',
    marginBottom: '6px'
  },
  topicStats: {
    fontSize: '14px',
    color: '#666666',
    fontFamily: '"Open Sans", sans-serif',
    letterSpacing: '0.4px',
    lineHeight: '16px'
  },
  arrowIcon: {
    color: '#2c5aa0',
    fontSize: '24px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    border: '1px solid #e0e0e0'
  },
  emptyIcon: {
    fontSize: '48px',
    color: '#cccccc',
    marginBottom: '16px'
  },
  emptyText: {
    fontSize: '16px',
    color: '#666666',
    marginBottom: '20px'
  },
  retryButton: {
    backgroundColor: '#2c5aa0',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  }
};

export default StudyPack;